import React from 'react';
import { Clock, User, AlertCircle, GripVertical } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const TaskCard = ({ task, onTaskClick }) => {
    // Parse deadline
    const getDeadlineInfo = () => {
        if (!task.deadline) return null;

        const deadlineTs = typeof task.deadline === 'object' ? task.deadline.timestamp : task.deadline;
        if (!deadlineTs) return null;

        const deadlineDate = new Date(deadlineTs);
        const now = new Date();
        const isOverdue = deadlineDate < now;

        const formatDate = (date) => {
            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            return `${day}.${month}`;
        };

        return {
            date: formatDate(deadlineDate),
            isOverdue,
            fullDate: deadlineDate.toLocaleDateString('ru-RU')
        };
    };

    const deadlineInfo = getDeadlineInfo();

    // Get assignee initials
    const getInitials = (name) => {
        if (!name) return '?';
        const parts = name.split(' ');
        if (parts.length >= 2) {
            return parts[0][0] + parts[1][0];
        }
        return name.slice(0, 2).toUpperCase();
    };

    const assigneeName = task.assignee_name || task.assigneeName || 'Не назначено';
    const assignees = assigneeName !== 'Не назначено' ? String(assigneeName).split(', ').filter(Boolean) : ['Не назначено'];

    const getPriorityColor = (p) => {
        switch (p) {
            case 'Critical': return 'text-rose-500 bg-rose-500/10 border-rose-500/20';
            case 'High': return 'text-amber-500 bg-amber-500/10 border-amber-500/20';
            case 'Medium': return 'text-blue-500 bg-blue-500/10 border-blue-500/20';
            case 'Low': return 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20';
            default: return 'text-neutral-500 bg-neutral-500/10 border-neutral-500/20';
        }
    };

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({
        id: task.id,
        data: {
            status: task.status
        }
    });

    const style = {
        transform: CSS.Translate.toString(transform),
        transition,
        opacity: isDragging ? 0.3 : 1,
        zIndex: isDragging ? 50 : 'auto',
        position: 'relative'
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`bg-neutral-800/40 border ${isDragging ? 'border-blue-500 shadow-xl shadow-blue-500/10' : 'border-neutral-700'} rounded-2xl p-4 hover:bg-neutral-800/60 transition-all group relative`}
        >
            {/* Drag Handle (Visible on hover or mobile) */}
            <div
                {...attributes}
                {...listeners}
                className="absolute top-4 right-4 p-1 text-neutral-600 hover:text-white cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
            >
                <GripVertical className="w-4 h-4" />
            </div>

            <div
                onClick={() => onTaskClick && onTaskClick(task)}
                className="cursor-pointer"
            >
            {/* Priority Badge */}
            <div className="flex items-center justify-between mb-2">
                <div className="flex gap-1.5 flex-wrap">
                    {task.priority && (
                        <div className={`px-2 py-0.5 rounded-full border text-[8px] font-black uppercase tracking-widest ${getPriorityColor(task.priority)}`}>
                            {task.priority}
                        </div>
                    )}
                </div>
                {task.is_hot && (
                    <div className="w-2 h-2 rounded-full bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.6)] animate-pulse" title="Срочная задача" />
                )}
            </div>

            {/* Task Title */}
            <h4 className="text-white font-bold text-[13px] leading-tight mb-2 line-clamp-2 group-hover:text-blue-400 transition-colors">
                {task.title}
            </h4>

            {/* ERP Meta (Project/Service) */}
            {(task.project_name || task.service_name) && (
                <div className="flex flex-col gap-1 mb-3">
                    {task.project_name && (
                        <div className="text-[10px] text-neutral-500 font-bold uppercase truncate">
                            <span className="text-blue-500">📁</span> {task.project_name}
                        </div>
                    )}
                    {task.service_name && (
                        <div className="text-[9px] text-neutral-400 font-medium truncate opacity-70">
                            └─ {task.service_name}
                        </div>
                    )}
                </div>
            )}

            {/* Assignees */}
            <div className="flex items-center gap-2 mb-2">
                <div className="flex -space-x-2">
                    {assignees.map((name, idx) => (
                        <div key={idx} title={name} className="w-6 h-6 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-[10px] font-black text-white ring-2 ring-neutral-800 relative z-[1]">
                            {getInitials(name)}
                        </div>
                    ))}
                </div>
                <span className="text-[11px] text-neutral-400 font-medium truncate">
                    {assignees.length > 1 ? `${assignees.length} исполнителя` : assignees[0]}
                </span>
            </div>

            {/* Column Tag */}
            {task.columnName && (
                <div className="inline-block px-2 py-1 bg-neutral-700/50 rounded-lg mb-2">
                    <span className="text-[10px] font-bold text-neutral-300 uppercase tracking-wider">
                        {task.columnName}
                    </span>
                </div>
            )}

            {/* Deadline */}
            {deadlineInfo && (
                <div className={`flex items-center gap-1 mt-2 ${deadlineInfo.isOverdue ? 'text-red-400' : 'text-neutral-400'}`}>
                    {deadlineInfo.isOverdue && <AlertCircle className="w-3 h-3" />}
                    <Clock className="w-3 h-3" />
                    <span className="text-[11px] font-bold">
                        {deadlineInfo.date}
                        {deadlineInfo.isOverdue && ' (просрочено)'}
                    </span>
                </div>
            )}
            </div>
        </div>
    );
};

export default TaskCard;
